import * as React from 'react';
import debounce from 'debounce';
import config from '@config';

import { removeEmptyKeys } from '@util/ApiUtil';
import { formatClock, formatClockless } from '@util/DateUtil';
import { getEnumValue } from '@util/KeyVal';
import { getServiceInfo } from '@util/TextUtil';
import { inject, observer } from 'mobx-react';

import { Disturbance, DisturbanceArea, DisturbanceLocation, DisturbanceStatus, DisturbanceTableRows, DisturbanceType } from '@config/DisturbanceConfig';
import { FilterState } from '@config/DisturbanceFilter';
import DisturbancePagination from '@disturbance/DisturbancePagination';
import ChangeDisturbanceStore from '@stores/ChangeDisturbanceStore';
import DisturbanceLocationModalStore from '@stores/DisturbanceLocationModalStore';
import DisturbanceModalStore from '@stores/DisturbanceModalStore';
import FilterStore from '@stores/FilterStore';
import IncidentDisturbanceStore from '@stores/IncidentDisturbanceStore';
import LanguageStore from '@stores/LanguageStore';
import ProblemDisturbanceStore from '@stores/ProblemDisturbanceStore';
import TranslateStore from '@stores/TranslateStore';

import {
  Anchor,
  Button,
  Grid,
  GridColumn,
  Heading,
  Notice,
  Separator,
  Spinner,
  Tag,
  Text,
  Tooltip,
  TooltipToggle,
  Icon,
  TableCell,
  TableRow,
  TableCollapse,
  TableCollapseTrigger
} from 'telia-front-react';

export interface DisturbanceTableProps {
  heading: string;
  rows: DisturbanceTableRows;
  type: DisturbanceType;
  store: ChangeDisturbanceStore | IncidentDisturbanceStore | ProblemDisturbanceStore;
  isLoading: boolean;
  employee: boolean;
  filter: FilterState;
  isDealer: boolean;
  hasIvrRole: boolean;
  disturbanceModalStore?: DisturbanceModalStore;
  disturbanceLocationModalStore?: DisturbanceLocationModalStore;
  filterStore?: FilterStore;
  translateStore?: TranslateStore;
  languageStore?: LanguageStore;
}

@inject(
  'heading',
  'rows',
  'type',
  'isLoading',
  'store',
  'employee',
  'disturbanceModalStore',
  'filter',
  'isDealer',
  'hasIvrRole',
  'filterStore',
  'translateStore',
  'languageStore',
  'disturbanceLocationModalStore'
)
@observer
export default class DisturbanceTable extends React.Component<DisturbanceTableProps> {
  public async componentDidMount() {
    await this.fetchDisturbances(this.props.filter);
  }

  public componentWillUnmount() {
    this.props.store.resetDisturbances();
  }

  public async componentDidUpdate(prevProps: DisturbanceTableProps) {
    const { filter, type, isDealer } = this.props;
    const filterNew = JSON.parse(JSON.stringify(filter));
    if (type === DisturbanceType.ProblemDisturbanceInfo) {
      delete filterNew.customerId;
      delete filterNew.region;
    }
    if (type !== DisturbanceType.ProblemDisturbanceInfo && filterNew.status && filterNew.status.includes(DisturbanceStatus.DEADEND)) {
      filterNew.status = filterNew.status.filter((status: string) => status !== DisturbanceStatus.DEADEND);
    }
    if ((prevProps.filter && this.compareFilterObject(filterNew, prevProps.filter, type)) || isDealer !== prevProps.isDealer) {
      await this.fetchDisturbances(filterNew, 0);
    }
  }

  private compareFilterObject(filter: FilterState, prevFilter: FilterState, type: DisturbanceType) {
    let filterPrev = JSON.parse(JSON.stringify(prevFilter));
    const filterNew = removeEmptyKeys(filter);
    if (type === DisturbanceType.ProblemDisturbanceInfo) {
      delete filterPrev.customerId;
      delete filterPrev.region;
    }
    if (type !== DisturbanceType.ProblemDisturbanceInfo && filterPrev.status && filterPrev.status.includes(DisturbanceStatus.DEADEND)) {
      filterPrev.status = filterPrev.status.filter((status: string) => status !== DisturbanceStatus.DEADEND);
    }
    filterPrev = removeEmptyKeys(filterPrev);
    return JSON.stringify(filterNew) !== JSON.stringify(filterPrev);
  }

  private fetchDisturbances = debounce(async (filter: FilterState, offset?: number): Promise<void> => {
    const { store } = this.props;
    if (offset === undefined) {
      offset = store && store.disturbances !== undefined ? store.disturbances.offset.currentPage : 0;
    }
    if (store.apiCall !== DisturbanceType.ProblemDisturbanceInfo || (store.apiCall === DisturbanceType.ProblemDisturbanceInfo && this.props.isDealer)) {
      await store.fetchDisturbances(filter, offset);
    }
  }, 500);

  public render(): JSX.Element {
    const {
      translateStore: { translate },
      isDealer,
      filter,
      store,
      heading,
      rows,
      isLoading
    } = this.props;

    return (
      <>
        {!isDealer && (
          <>
            <Separator spacing="xsmall" color="invisible" />
            <Grid align={['between-xs']}>
              <GridColumn width={['xs']}>
                <Heading element="h3" className="h4 text-bold">
                  {heading}
                </Heading>
              </GridColumn>
              <GridColumn width={['xs']}>* {translate('table.approximate.time')}</GridColumn>
            </Grid>
          </>
        )}
        {isLoading ? (
          <>
            <Separator color="invisible" spacing="medium" />
            <Spinner />
          </>
        ) : !rows || rows.data == null ? (
          <>
            <Separator color="invisible" spacing="small" />
            <Notice type="error" direction="vertical">
              <Button text={translate('general.refresh')} onClick={() => this.fetchDisturbances(filter)} />
            </Notice>
          </>
        ) : rows.data.length === 0 ? (
          !isDealer ? (
            <>
              <Separator color="invisible" spacing="xsmall" />
              <Notice type="info" direction="horizontal">
                {rows.type === DisturbanceType.IncidentDisturbanceInfo ? translate('table.incident.no') : translate('table.change.no')}{' '}
                <Anchor url={config.controlUrl}>{translate('table.service.check')}</Anchor>.
              </Notice>
            </>
          ) : (
            <>
              <Separator color="invisible" spacing="small" />
              <Notice type="employee" direction="horizontal">
                {translate('table.filter.no')}
              </Notice>
            </>
          )
        ) : (
          <>
            {isDealer && (
              <>
                <Separator spacing="xsmall" color="invisible" />
                <Grid align={['between-xs']}>
                  <GridColumn width={['xs']}>* {translate('table.approximate.time')}</GridColumn>
                  <GridColumn width={['xs']}>
                    <DisturbancePagination store={store} filter={filter} />
                  </GridColumn>
                </Grid>
              </>
            )}
            <Separator spacing="xsmall" color="invisible" />
            <TableCollapse break="lg" responsive="cols" stickyHeader="primary-3">
              <thead>
                <TableRow className="table__row table__row--header">
                  <TableCell type="th" className="table__cell table__cell--2" scope="col">
                    ID
                  </TableCell>
                  <TableCell type="th" className="table__cell" scope="col">
                    {translate('table.start')}
                  </TableCell>
                  <TableCell type="th" className="table__cell" scope="col">
                    {translate('table.end')}
                  </TableCell>
                  {isDealer && (
                    <TableCell type="th" className={'table__cell table__cell--4'} scope="col">
                      {translate('table.title.description')}
                    </TableCell>
                  )}
                  {isDealer && (
                    <TableCell type="th" className="table__cell table__cell--4" scope="col">
                      {rows.type === DisturbanceType.ProblemDisturbanceInfo ? translate('table.workaround') : translate('disturbance.related.entity')}
                    </TableCell>
                  )}
                  {!isDealer && (
                    <TableCell type="th" className="table__cell table__cell--4" scope="col">
                      {translate('table.location')}
                    </TableCell>
                  )}
                  {isDealer && <TableCell type="th" className="table__cell" scope="col" />}
                </TableRow>
              </thead>
              <tbody>
                {rows.data.map((row, key) => {
                  return new Array(2).fill(undefined).map((step, index) => {
                    if (index === 0) {
                      return this.getTableRow(row, key);
                    } else {
                      return this.getTableRowExpanded(row, key);
                    }
                  });
                })}
              </tbody>
            </TableCollapse>
            <Separator spacing="xsmall" color="invisible" />
            <Grid align={['end-xs']}>
              <GridColumn width={['xs']}>
                <DisturbancePagination store={store} filter={filter} />
              </GridColumn>
            </Grid>
          </>
        )}
      </>
    );
  }

  private getId(data: Disturbance, type: DisturbanceType): JSX.Element {
    const {
      translateStore: { translate },
      isDealer,
      disturbanceModalStore,
      hasIvrRole
    } = this.props;

    return isDealer ? (
      <Grid gutter="xsmall">
        <GridColumn width={['xs-12']}>
          <Button
            text={data.id}
            link={true}
            type="employee"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              disturbanceModalStore.openModal(data, type);
            }}
          />
        </GridColumn>
        <GridColumn width={['xs-12']}>
          <Grid gutter="xsmall">{data.areas.map((area, areaKey) => DisturbanceTable.getArea(area, areaKey))}</Grid>
        </GridColumn>

        {(hasIvrRole || data.affected > 0 || data.hiddenFromCustomer) && (
          <GridColumn width={['xs-12']}>
            <Grid align={['middle-xs']} gutter="small">
              {hasIvrRole && (
                <div style={{ padding: '0 4px' }}>
                  <Button
                    text={'IVR'}
                    link={true}
                    url={`${config.ivrUrl}?ID=${data.id}`}
                    target="_blank"
                    type="employee"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  />
                </div>
              )}
              {data.affected > 0 && (
                <div style={{ padding: '0 4px' }}>
                  <Grid align={['middle-xs']} noWrap={true} gutter="none">
                    <div>
                      <TooltipToggle className="text-bold text-color-normal" color="employee" id={`affected-${data.id}`}>
                        <Icon size="xs" modifiers="#people" align="none" />
                      </TooltipToggle>
                      <Tooltip referenceElement={`#affected-${data.id}`} color="#fff">
                        {translate('table.affected.clients')}: {data.affected}
                      </Tooltip>
                    </div>
                    <div className="text-bold">&nbsp;{data.affected}</div>
                  </Grid>
                </div>
              )}
              {data.hiddenFromCustomer && (
                <div style={{ padding: '0 4px' }}>
                  <TooltipToggle className="text-bold text-color-normal" color="employee" id={`hiddenFromCustomer-${data.id}`}>
                    <Icon size="xs" modifiers="#password-hide" align="none" />
                  </TooltipToggle>
                  <Tooltip referenceElement={`#hiddenFromCustomer-${data.id}`} color="#fff">
                    {translate('table.hiddenFromCustomer')}
                  </Tooltip>
                </div>
              )}
            </Grid>
          </GridColumn>
        )}
      </Grid>
    ) : (
      <>{data.id}</>
    );
  }

  private static getArea(area: string, key: number): JSX.Element {
    return (
      <GridColumn key={key} width={['xs']}>
        <Tag text={getEnumValue(DisturbanceArea, area)} size="tiny" purpose="employee" />
      </GridColumn>
    );
  }

  private getLocation(row: Disturbance, type: DisturbanceType): JSX.Element {
    const {
      translateStore: { translate },
      disturbanceLocationModalStore,
      filterStore
    } = this.props;
    const location: DisturbanceLocation[] = row.location;
    const id: string = row.id;
    const distinctLocations = (array: DisturbanceLocation[]) => {
      array = array || [];
      const result = [];
      const map = new Map();
      for (const loc of array) {
        if (!map.has(loc.name)) {
          map.set(loc.name, true); // set any value to Map
          result.push({
            name: loc.name,
            f4g: loc.f4g,
            serviceName: loc.serviceName,
            serviceId: loc.serviceId
          });
        }
      }
      return result;
    };

    const locationModalLink = (locValue: DisturbanceLocation) => {
      return filterStore.selectedClientOnly &&
        disturbanceLocationModalStore.customerId &&
        locValue.serviceName &&
        location.filter((loc) => loc.name === locValue.name).length > 0 ? (
        <Anchor
          url="#"
          onClick={(e) => {
            e.preventDefault();
            disturbanceLocationModalStore.openModal(row, locValue, type);
          }}
        >
          {' '}
          ({location.filter((loc) => loc.name === locValue.name).length})
        </Anchor>
      ) : (
        <></>
      );
    };

    const locationName = (locKey: number, locValue: DisturbanceLocation) => {
      return locValue.f4g ? (
        <Grid key={locKey} gutter="xsmall" noWrap={true}>
          <GridColumn width={['max-xs']}>
            <p className="text-weight-normal">
              {locValue.name} {locationModalLink(locValue)}
            </p>
          </GridColumn>
        </Grid>
      ) : (
        <p key={locKey}>
          {locValue.name} {locationModalLink(locValue)}
        </p>
      );
    };

    const distinctLocation = distinctLocations(location);

    if (distinctLocation.length < 5) {
      return <>{distinctLocation.map((locationValue, locationKey) => locationName(locationKey, locationValue))}</>;
    }
    const tooltipId = `location-${id}`;
    return (
      <>
        {distinctLocation.slice(0, 4).map((locationValue, locationKey) => locationName(locationKey, locationValue))}
        <TooltipToggle className="text-bold" id={tooltipId}>
          {translate('table.read.more')}
        </TooltipToggle>
        <Tooltip referenceElement={`#${tooltipId}`} size="large" color="#fff">
          {distinctLocation.map((locationValue, locationKey) => (
            <p key={locationKey}>{locationValue.name}</p>
          ))}
        </Tooltip>
      </>
    );
  }

  private startTimeWithTooltip(data: Disturbance): JSX.Element {
    const {
      rows: { type }
    } = this.props;
    if (type === DisturbanceType.ChangeDisturbanceInfo) {
      if (!data.start && data.plannedStart) {
        return <>{DisturbanceTable.getTime(data.plannedStart)}&nbsp;*</>;
      }
    }
    return DisturbanceTable.getTime(data.start);
  }

  private endTimeWithTooltip(data: Disturbance): JSX.Element {
    if (!data.end && data.plannedEnd) {
      return <>{DisturbanceTable.getTime(data.plannedEnd)}&nbsp;*</>;
    }
    return DisturbanceTable.getTime(data.end);
  }

  private static getTime(date: string): JSX.Element {
    return date ? (
      <span>
        {formatClockless(date)} {formatClock(date)}
      </span>
    ) : null;
  }

  private getDescriptionText(row: Disturbance): string {
    if (!row.description && (row.serviceInfo === null || !row.serviceInfo.length)) {
      return '';
    }
    const {
      languageStore: { currentLanguage }
    } = this.props;
    const serviceInfoText = getServiceInfo(row.serviceInfo, currentLanguage);
    return serviceInfoText === row.description ? serviceInfoText : `${serviceInfoText} ${row.description}`;
  }

  private getDescription(row: Disturbance): JSX.Element {
    const {
      languageStore: { currentLanguage }
    } = this.props;
    const serviceInfoText = this.getDescriptionText(row);

    if (!serviceInfoText) {
      return null;
    }

    if (serviceInfoText.length <= 148) {
      return (
        <span>
          {serviceInfoText && <p className="text-weight-normal">{getServiceInfo(row.serviceInfo, currentLanguage)}</p>}
          {serviceInfoText !== row.description && <p className="text-weight-normal">{row.description}</p>}
        </span>
      );
    }
    let displayText = '';
    if (serviceInfoText.length <= 148) {
      displayText = row.description.substring(0, 148 - serviceInfoText.length);
    } else {
      displayText = serviceInfoText.substring(0, 148);
    }
    const indexOfSpace = displayText.lastIndexOf(' ');

    return (
      <>
        <span className="text-weight-normal">
          {serviceInfoText.length <= 148 ? (
            <span>
              {serviceInfoText && <p className="text-break">{serviceInfoText}</p>}
              {<p className="text-break">{row.description.substring(0, indexOfSpace)}</p>}
            </span>
          ) : (
            serviceInfoText && <p className="text-break">{serviceInfoText.substring(0, indexOfSpace)}</p>
          )}
        </span>
      </>
    );
  }

  private getDescriptionLeftPart(row: Disturbance): JSX.Element {
    const serviceInfoText = this.getDescriptionText(row);
    if (!serviceInfoText) {
      return null;
    }
    if (serviceInfoText.length >= 148) {
      const displayText = serviceInfoText.substring(0, 148);
      const indexOfSpace = displayText.lastIndexOf(' ');
      return <p className="text-weight-normal text-break">{serviceInfoText.substring(indexOfSpace + 1)}</p>;
    } else {
      return null;
    }
  }

  private getWorkaround(row: Disturbance): JSX.Element {
    if (!row.workaround || !row.workaround.length) {
      return null;
    }
    let displayText = '';
    displayText = row.workaround.substring(0, 160);
    const indexOfSpace = displayText.lastIndexOf(' ');
    return <p className="text-weight-normal text-break">{row.workaround.substring(0, indexOfSpace)}</p>;
  }

  private getWorkaroundLeftPart(row: Disturbance): JSX.Element {
    if (!row.workaround || !row.workaround.length) {
      return null;
    }
    if (row.workaround.length >= 160) {
      const displayText = row.workaround.substring(0, 160);
      const indexOfSpace = displayText.lastIndexOf(' ');
      return <p className="text-weight-normal text-break">{row.workaround.substring(indexOfSpace + 1)}</p>;
    } else {
      return null;
    }
  }

  private getRelatedEntity(row: Disturbance): JSX.Element {
    let relatedEntityText = '';
    if (row.relatedEntity) {
      row.relatedEntity.map(
        (entity, index) =>
          (relatedEntityText = relatedEntityText.concat(
            index < 3 && relatedEntityText.length < 170
              ? `${entity.siaId}${entity.address ? ` (${entity.address})` : ''}${index < row.relatedEntity.length - 1 ? '\n' : ''}`
              : ''
          ))
      );
    }
    return (
      <>
        {relatedEntityText.split('\n').map((text: string, key: number) => {
          return (
            <p className="text-weight-normal" key={key}>
              {text}
            </p>
          );
        })}
      </>
    );
  }

  private getRelatedEntityLeftPart(row: Disturbance): string {
    let relatedEntityText = '';
    let relatedEntityText2 = '';
    if (row.relatedEntity) {
      row.relatedEntity.map((entity, index) => {
        const entityText = `${entity.siaId}${entity.address ? ` (${entity.address})` : ''}${index < row.relatedEntity.length - 1 ? '\n' : ''}`;
        if (index < 3 && relatedEntityText.length < 170) {
          relatedEntityText = relatedEntityText.concat(entityText);
        } else {
          relatedEntityText2 = relatedEntityText2.concat(entityText);
        }
      });
    }
    return relatedEntityText2;
  }

  private isExpandable(row: Disturbance): boolean {
    const description = this.getDescriptionText(row).length > 148;
    const workaround: boolean = row.workaround && row.workaround.length > 160;
    const elements = this.getRelatedEntityLeftPart(row).length > 0;
    return description || workaround || elements;
  }

  private getTableRow(row: Disturbance, key: number): JSX.Element {
    const {
      rows: { type },
      isDealer
    } = this.props;
    row.isExpandable = this.isExpandable(row);
    const rowClassArray = ['table__row', 'text-small', row.isExpandable ? 'table-collapse__row--trigger-expandable' : ''];
    const cellClassArray = ['table__cell', 'table__cell--valign-top'];
    if (row.state === 'inactive') {
      rowClassArray.push('text-inactive');
    } else {
      rowClassArray.push('text-bold');
    }

    return (
      <TableRow key={key} className={rowClassArray.join(' ')} rowTrigger={row.isExpandable ? `#tbl-collapse-${row.id}` : null}>
        <TableCell className={cellClassArray.join(' ')}>{this.getId(row, type)}</TableCell>
        <TableCell className={cellClassArray.join(' ')}>{this.startTimeWithTooltip(row)}</TableCell>
        <TableCell className={cellClassArray.join(' ')}>{this.endTimeWithTooltip(row)}</TableCell>
        {isDealer && (
          <TableCell className={[...cellClassArray].join(' ')}>
            <Text>
              <p className="text-break">
                <b>{row.heading}</b>
              </p>
              {this.getDescription(row)}
            </Text>
          </TableCell>
        )}
        {isDealer && (
          <TableCell className={cellClassArray.join(' ')}>
            {type === DisturbanceType.ProblemDisturbanceInfo ? this.getWorkaround(row) : this.getRelatedEntity(row)}
          </TableCell>
        )}
        {!isDealer && (
          <TableCell className={cellClassArray.join(' ')}>
            <Text>{this.getLocation(row, type)}</Text>
          </TableCell>
        )}
        {isDealer && (
          <TableCell className={cellClassArray.join(' ')}>
            {row.isExpandable && <TableCollapseTrigger text="" url={`#tbl-collapse-${row.id}`} link={true} iconRight="#arrow-down" />}
          </TableCell>
        )}
      </TableRow>
    );
  }

  private getTableRowExpanded(row: Disturbance, key: number): JSX.Element {
    const {
      rows: { type },
      isDealer
    } = this.props;

    const rowClassArray = ['table__row', 'text-small'];
    const cellClassArray = ['table__cell', 'table__cell--valign-top'];
    if (row.state === 'inactive') {
      rowClassArray.push('text-inactive');
    } else {
      rowClassArray.push('text-bold');
    }

    return (
      row.isExpandable &&
      isDealer && (
        <TableRow id={`#tbl-collapse-${row.id}`} key={`#tbl-collapse-key-${row.id}`} className={rowClassArray.join(' ')} collapsible={true}>
          <TableCell className={cellClassArray.join(' ')} />
          <TableCell className={cellClassArray.join(' ')} />
          <TableCell className={cellClassArray.join(' ')} />
          <TableCell className={[...cellClassArray].join(' ')}>
            <Text>{this.getDescriptionLeftPart(row)}</Text>
          </TableCell>
          <TableCell className={cellClassArray.join(' ')}>
            {type === DisturbanceType.ProblemDisturbanceInfo
              ? this.getWorkaroundLeftPart(row)
              : this.getRelatedEntityLeftPart(row)
                  .split('\n')
                  .map((text: string, key: number) => {
                    return (
                      <p className="text-weight-normal" key={key}>
                        {text}
                      </p>
                    );
                  })}
          </TableCell>
          <TableCell />
        </TableRow>
      )
    );
  }
}
